import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Bars3Icon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ADMIN_ROUTES } from '../routes';

interface AdminHeaderProps {
  onMenuClick: () => void;
}

/** Generate breadcrumbs from the current path using route metadata. */
function getBreadcrumbs(pathname: string): Array<{ label: string; path: string }> {
  const crumbs: Array<{ label: string; path: string }> = [];

  // Find matching route
  const matched = ADMIN_ROUTES.find((r) => {
    if (r.path === '/dashboard/super-admin') return pathname === r.path;
    return pathname === r.path || pathname.startsWith(r.path + '/');
  });

  if (matched) {
    // Always start with "Platform"
    crumbs.push({ label: 'Platform', path: '/dashboard/super-admin' });

    // Add the matched route if it's not already the overview
    if (matched.path !== '/dashboard/super-admin') {
      crumbs.push({ label: matched.label, path: matched.path });
    }
  }

  return crumbs;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ onMenuClick }) => {
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);

  // Determine environment label
  const envLabel = process.env.NODE_ENV === 'production' ? 'Production' : 'Development';
  const envColor = process.env.NODE_ENV === 'production' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 lg:px-6 flex items-center justify-between gap-4">
      {/* Left: Mobile menu + breadcrumbs */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Open menu"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>

        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1 text-sm text-slate-500 min-w-0">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.path}>
              {i > 0 && <ChevronRightIcon className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
              {i < breadcrumbs.length - 1 ? (
                <Link to={crumb.path} className="hover:text-slate-700 truncate transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="font-medium text-slate-800 truncate">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Mobile: current page title */}
        <span className="sm:hidden font-medium text-slate-800 truncate">
          {breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : 'Platform'}
        </span>
      </div>

      {/* Right: Environment badge + help */}
      <div className="flex items-center gap-3 shrink-0">
        <span className={`hidden md:inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${envColor}`}>
          {envLabel}
        </span>
        <a
          href="mailto:support@esanza.com"
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
          title="Help & Support"
        >
          Help
        </a>
      </div>
    </header>
  );
};

export default AdminHeader;
